import { notifyOnError, throttle } from '~/rage/decorators';
import { IObservable, SeenTracker, Subject } from '~/rage/utils';
import {
	IVehicleShopOffer,
	IVehicleTuning,
	IVehicleWheelsSettings,
	IVehicleWheelsSettingsRanges,
	isStockTuningId,
	isTuningItemCompatibleWithKit,
	VehicleTuningCategory,
	VehicleTuningSubcategory,
	VehicleWheelTireProfile,
	VehicleWheelsSetting,
	WalletType,
} from '~/shared';
import { BackendService } from '../BackendService';
import { GameConfigService } from '../GameConfigService';
import { RpcService } from '../RpcService';
import { VarsService } from '../VarsService/VarsService';
import { IVehicleService } from '../VehicleService';
import type {
	ICharacterOwnedFreeTuning,
	IVehicleTuningShopItem,
	IVehicleTuningShopItemVariant,
	IVehicleTuningShopService,
} from './vehicleTuningShopServiceTypes';

export class VehicleTuningShopService implements IVehicleTuningShopService {
	private readonly items = new Subject<IVehicleTuningShopItem[]>([]);
	private readonly ownedItems = new Subject<string[]>([]);
	private readonly ownedWheelsSettings =
		new Subject<IVehicleWheelsSettings | null>(null);

	private readonly ownedFreeTuning = new Subject<ICharacterOwnedFreeTuning>({
		ownedItems: [],
		ownedKits: [],
	});

	private readonly wheelsSettingsRanges =
		new Subject<IVehicleWheelsSettingsRanges | null>(null);

	private readonly wheelTireProfile =
		new Subject<VehicleWheelTireProfile | null>(null);

	private readonly wheelsSetupPrice = new Subject(0);
	private readonly tireProfileChangePrice = new Subject(0);
	private temporaryKitId: string | null | undefined;
	private seenTracker = new SeenTracker('vehicle_tuning_shop');
	private shopOffer = new Subject<IVehicleShopOffer | null>(null);

	constructor(
		private readonly backend: BackendService,
		private readonly gameConfig: GameConfigService,
		private readonly vehicles: IVehicleService,
		private readonly rpc: RpcService,
		private readonly vars: VarsService,
	) {
		this.gameConfig.getConfigs().subscribe(() => {
			this.updateItemsKits();
			this.updateWheelsSettings();
		});

		this.getOwnedItems().subscribe(() => {
			this.updateItemsKits();
		});

		this.vehicles.getLocalVehicleModel().subscribe(() => {
			this.updateItemsKits();
			this.updateWheelsSettings();
		});

		this.vars
			.get<IVehicleShopOffer | null>('tuningShop:offer', null)
			.subscribe((offer) => {
				this.shopOffer.next(offer);
			});
	}

	@notifyOnError
	public async buy(
		tuning: Partial<IVehicleTuning>,
		wallet: WalletType,
	): Promise<void> {
		const vehicleId = this.vehicles.getLocalOwnedVehicleId().getValue();
		await this.backend.invoke('vehicleTuning:buy', {
			vehicleId,
			tuning,
			wallet,
		});

		this.temporaryKitId = undefined;
		await this.fetchOwnedItemsSettings();
	}

	public getShopItems(): IObservable<IVehicleTuningShopItem[]> {
		return this.items;
	}

	@notifyOnError
	public async setCameraFacing(
		shopCategory: VehicleTuningCategory | null,
		shopSubcategory?: VehicleTuningSubcategory | null,
	): Promise<void> {
		await this.rpc.invokeClient('vehicleTuningShop:setCameraFacing', {
			shopCategory,
			shopSubcategory,
		});
	}

	@notifyOnError
	public async fetchOwnedItemsSettings(): Promise<void> {
		const vehicleId = this.vehicles.getLocalOwnedVehicleId().getValue();
		if (!vehicleId) {
			return;
		}

		const ownedTuning = await this.backend.invoke<Required<IVehicleTuning>>(
			'vehicleTuning:getOwned',
			{ vehicleId },
		);

		const ownedItems = [...ownedTuning.items, ownedTuning.itemsKit];
		this.ownedItems.next(ownedItems);

		this.ownedWheelsSettings.next(ownedTuning.wheels);
		this.wheelTireProfile.next(ownedTuning.tiresProfile ?? null);
	}

	private getOwnedItems(): IObservable<string[]> {
		return this.ownedItems;
	}

	public getOwnedWheelsSettings(): IObservable<IVehicleWheelsSettings | null> {
		return this.ownedWheelsSettings;
	}

	public getWheelsSettingsRanges(): IObservable<IVehicleWheelsSettingsRanges | null> {
		return this.wheelsSettingsRanges;
	}

	public getWheelTireProfile(): IObservable<VehicleWheelTireProfile | null> {
		return this.wheelTireProfile;
	}

	@notifyOnError
	public async setTiresProfilePreview(
		profile: VehicleWheelTireProfile,
	): Promise<void> {
		await this.rpc.invokeServer('vehicleTuningShop:setTiresProfile', {
			tiresProfile: profile,
		});
	}

	@notifyOnError
	public async resetTiresProfilePreview(): Promise<void> {
		await this.rpc.invokeServer('vehicleTuningShop:resetTiresProfile');
	}

	@notifyOnError
	@throttle(40)
	public async buyWheelTireProfile(
		profile: VehicleWheelTireProfile,
	): Promise<void> {
		const vehicleId = this.vehicles.getLocalOwnedVehicleId().getValue();
		if (!vehicleId) {
			return;
		}

		await this.backend.invoke('vehicleTuning:setWheelTireProfile', {
			vehicleId,
			wheelTireProfile: profile,
		});
		this.wheelTireProfile.next(profile);
	}

	@notifyOnError
	@throttle(100)
	public async installOwnedItem(itemId: string): Promise<void> {
		const vehicleId = this.vehicles.getLocalOwnedVehicleId().getValue();
		if (!vehicleId) {
			return;
		}

		await this.backend.invoke('vehicleTuning:installOwnedItem', {
			vehicleId,
			itemId,
		});

		await this.fetchOwnedItemsSettings();
		await this.fetchOwnedFreeTuning();
	}

	@notifyOnError
	@throttle(100)
	public async installItem(itemId: string | null): Promise<void> {
		await this.rpc.invokeServer('vehicleTuningShop:installItem', {
			itemId,
		});
	}

	@notifyOnError
	@throttle(100)
	public async installItemsKit(itemsKitId: string | null): Promise<void> {
		await this.rpc.invokeServer('vehicleTuningShop:installItemsKit', {
			itemsKitId,
		});
		this.temporaryKitId = itemsKitId ?? undefined;
		this.updateItemsKits();
	}

	@notifyOnError
	@throttle(40)
	public async setWheelsSettings(
		wheelsSettings: Partial<IVehicleWheelsSettings>,
	): Promise<void> {
		await this.rpc.invokeClient(
			'vehicleTuningShop:setWheelsSettings',
			wheelsSettings,
		);
	}

	@notifyOnError
	@throttle(100)
	public async resetWheelsSettings(): Promise<void> {
		await this.rpc.invokeClient('vehicleTuningShop:resetWheelsSettings');
	}

	@notifyOnError
	@throttle(100)
	public async removeTemporaryItems(): Promise<void> {
		await this.rpc.invokeServer('vehicleTuningShop:removeTemporaryItems');
		this.temporaryKitId = undefined;
		this.updateItemsKits();
	}

	@notifyOnError
	@throttle(100)
	public async resetTuning(): Promise<void> {
		const vehicleId = this.vehicles.getLocalOwnedVehicleId().getValue();
		if (!vehicleId) {
			return;
		}

		await this.backend.invoke('vehicleTuning:reset', { vehicleId });

		await Promise.all([
			this.removeTemporaryItems(),
			this.resetWheelsSettings(),
		]);

		await this.fetchOwnedItemsSettings();
	}

	public async exitMenu(): Promise<void> {
		setTimeout(() => {
			this.ownedItems.next([]);
		}, 1000);

		await Promise.all([
			this.removeTemporaryItems(),
			this.resetWheelsSettings(),
		]);

		await this.rpc.invokeClient('vehicleTuningShop:exitMenu');
	}

	private updateItemsKits(): void {
		if (!this.gameConfig.configs) {
			return;
		}

		const vehicleModel = this.vehicles.getLocalVehicleModel().getValue();

		const installedItems = this.ownedItems.getValue();
		const installedSet = new Set(installedItems);

		const items: IVehicleTuningShopItem[] = [];

		const kits: IVehicleTuningShopItem[] = [];

		const configItemsKits =
			this.gameConfig.configs.vehicleTuning?.itemsKits ?? [];
		const installedCustomKitId =
			configItemsKits.find(
				(kit) => installedSet.has(kit.id) && !isStockTuningId(kit.id),
			)?.id ?? null;
		const activeCustomKitId =
			this.temporaryKitId === undefined
				? installedCustomKitId
				: isStockTuningId(this.temporaryKitId)
				? null
				: this.temporaryKitId;
		for (const kit of configItemsKits) {
			if (kit.vehicleModel && kit.vehicleModel !== vehicleModel) {
				continue;
			}

			if (!kit.isShopItem) {
				continue;
			}

			kits.push({
				id: kit.id,
				name: kit.name,
				categoryId: VehicleTuningCategory.KITS,
				price: kit.price,
				isInstalled: installedSet.has(kit.id),
			});
		}

		const configItems = this.gameConfig.configs.vehicleTuning?.items ?? [];

		type ShopTuningItem = (typeof configItems)[number] & {
			shopCategory: VehicleTuningCategory;
		};
		const isShopTuningItem = (
			item: (typeof configItems)[number],
		): item is ShopTuningItem => item.isShopItem && item.shopCategory !== null;
		const variantGroups = new Map<string, ShopTuningItem[]>();
		const processedBaseIds = new Set<string>();

		for (const item of configItems) {
			if (item.vehicleModel && item.vehicleModel !== vehicleModel) {
				continue;
			}

			if (!isShopTuningItem(item)) {
				continue;
			}
			if (!isTuningItemCompatibleWithKit(item, activeCustomKitId)) {
				continue;
			}

			if (item.variantBaseId) {
				if (!variantGroups.has(item.variantBaseId)) {
					variantGroups.set(item.variantBaseId, []);
				}
				variantGroups.get(item.variantBaseId)!.push(item);
			}
		}

		for (const item of configItems) {
			if (item.vehicleModel && item.vehicleModel !== vehicleModel) {
				continue;
			}

			if (!isShopTuningItem(item)) {
				continue;
			}
			if (!isTuningItemCompatibleWithKit(item, activeCustomKitId)) {
				continue;
			}

			if (item.variantBaseId) {
				const baseItemId = item.variantBaseId;

				if (!processedBaseIds.has(baseItemId)) {
					processedBaseIds.add(baseItemId);

					const hasBaseItem = configItems.some(
						(base) => !base.variantBaseId && base.id === baseItemId,
					);

					if (!hasBaseItem) {
						const groupVariants = variantGroups.get(baseItemId);
						if (groupVariants && groupVariants.length > 0) {
							const variants: IVehicleTuningShopItemVariant[] =
								groupVariants.map((variantItem) => ({
									id: variantItem.id,
									name: variantItem.name,
									price: variantItem.price,
									isInstalled: installedSet.has(variantItem.id),
								}));

							const anyInstalled = variants.some((v) => v.isInstalled);

							items.push({
								id: baseItemId,
								categoryId: groupVariants[0].shopCategory,
								subcategoryId: groupVariants[0].shopSubcategory ?? undefined,
								name: groupVariants[0].name,
								price: groupVariants[0].price,
								isInstalled: anyInstalled,
								variants,
							});
						}
					}
				}

				continue;
			}

			const groupVariants = variantGroups.get(item.id);
			if (groupVariants && groupVariants.length > 0) {
				const variants: IVehicleTuningShopItemVariant[] = groupVariants.map(
					(variantItem) => ({
						id: variantItem.id,
						name: variantItem.name,
						price: variantItem.price,
						isInstalled: installedSet.has(variantItem.id),
					}),
				);

				const anyInstalled =
					variants.some((v) => v.isInstalled) || installedSet.has(item.id);

				items.push({
					id: item.id,
					categoryId: item.shopCategory,
					subcategoryId: item.shopSubcategory ?? undefined,
					name: item.name,
					price: item.price,
					isInstalled: anyInstalled,
					variants,
				});
				continue;
			}

			items.push({
				id: item.id,
				categoryId: item.shopCategory,
				subcategoryId: item.shopSubcategory ?? undefined,
				name: item.name,
				price: item.price,
				isInstalled: installedSet.has(item.id),
			});
		}

		this.items.next([...items, ...kits]);
	}

	private updateWheelsSettings(): void {
		const tuningConfig = this.gameConfig.configs?.vehicleTuning;
		const wheelsSettings = tuningConfig?.wheelsSettings;

		const vehicleModel = this.vehicles.getLocalVehicleModel().getValue();
		const wheelsDefaults = vehicleModel
			? tuningConfig?.wheelsDefaults[vehicleModel]
			: undefined;

		if (!wheelsSettings || !wheelsDefaults) {
			this.wheelsSettingsRanges.next(null);
			return;
		}

		const wheelsFrontCamberMin = this.round4(
			wheelsDefaults.wheelCamber0 + wheelsSettings.wheelsCamberFrontMin,
		);
		const wheelsFrontCamberMax = this.round4(
			wheelsDefaults.wheelCamber0 + wheelsSettings.wheelsCamberFrontMax,
		);

		const wheelsRearCamberMin = this.round4(
			wheelsDefaults.wheelCamber2 + wheelsSettings.wheelsCamberRearMin,
		);
		const wheelsRearCamberMax = this.round4(
			wheelsDefaults.wheelCamber2 + wheelsSettings.wheelsCamberRearMax,
		);

		const wheelsFrontOffsetMin = this.round4(
			wheelsDefaults.wheelTrackWidth0 + wheelsSettings.wheelsOffsetFrontMin,
		);
		const wheelsFrontOffsetMax = this.round4(
			wheelsDefaults.wheelTrackWidth0 + wheelsSettings.wheelsOffsetFrontMax,
		);

		const wheelsRearOffsetMin = this.round4(
			wheelsDefaults.wheelTrackWidth2 + wheelsSettings.wheelsOffsetRearMin,
		);
		const wheelsRearOffsetMax = this.round4(
			wheelsDefaults.wheelTrackWidth2 + wheelsSettings.wheelsOffsetRearMax,
		);

		const wheelsRadiusMin = this.round4(
			wheelsDefaults.wheelRadius + wheelsSettings.wheelsRadiusMin,
		);
		const wheelsRadiusMax = this.round4(
			wheelsDefaults.wheelRadius + wheelsSettings.wheelsRadiusMax,
		);

		const wheelsWidthMin = this.round4(
			wheelsDefaults.wheelWidth + wheelsSettings.wheelsWidthMin,
		);
		const wheelsWidthMax = this.round4(
			wheelsDefaults.wheelWidth + wheelsSettings.wheelsWidthMax,
		);

		const wheelsSettingsRanges: IVehicleWheelsSettingsRanges = {
			[VehicleWheelsSetting.FRONT_CAMBER]: [
				wheelsFrontCamberMin,
				wheelsFrontCamberMax,
			],
			[VehicleWheelsSetting.REAR_CAMBER]: [
				wheelsRearCamberMin,
				wheelsRearCamberMax,
			],
			[VehicleWheelsSetting.FRONT_OFFSET]: [
				wheelsFrontOffsetMin,
				wheelsFrontOffsetMax,
			],
			[VehicleWheelsSetting.REAR_OFFSET]: [
				wheelsRearOffsetMin,
				wheelsRearOffsetMax,
			],
			[VehicleWheelsSetting.RADIUS]: [wheelsRadiusMin, wheelsRadiusMax],
			[VehicleWheelsSetting.WIDTH]: [wheelsWidthMin, wheelsWidthMax],
		};
		this.wheelsSettingsRanges.next(wheelsSettingsRanges);

		this.wheelsSetupPrice.next(wheelsSettings.wheelsSetupPrice);
		this.tireProfileChangePrice.next(
			wheelsSettings.tireProfileChangePrice ?? 0,
		);
	}

	private round4(value: number): number {
		return parseFloat(value.toFixed(4));
	}

	public getWheelsSetupPrice(): IObservable<number> {
		return this.wheelsSetupPrice;
	}

	public getTireProfileChangePrice(): IObservable<number> {
		return this.tireProfileChangePrice;
	}

	public isUnseen(path: string): boolean {
		return this.seenTracker.isUnseen(path);
	}

	public markSeen(path: string): void {
		this.seenTracker.markAsSeen(path);
	}

	public getShopOffer(): IObservable<IVehicleShopOffer | null> {
		return this.shopOffer;
	}

	public getIsOwned(itemId: string): boolean {
		const ownedFree = this.ownedFreeTuning.getValue();
		return (
			ownedFree.ownedItems.includes(itemId) ||
			ownedFree.ownedKits.includes(itemId)
		);
	}

	@notifyOnError
	public async fetchOwnedFreeTuning(): Promise<void> {
		const ownedFree = await this.backend.invoke<ICharacterOwnedFreeTuning>(
			'vehicleTuning:getOwnedFreeTuning',
		);
		this.ownedFreeTuning.next(ownedFree);
	}

	public getOwnedFreeTuning(): IObservable<ICharacterOwnedFreeTuning> {
		return this.ownedFreeTuning;
	}

	@notifyOnError
	@throttle(100)
	public async installOwnedKit(kitId: string): Promise<void> {
		const vehicleId = this.vehicles.getLocalOwnedVehicleId().getValue();
		if (!vehicleId) {
			return;
		}

		await this.backend.invoke('vehicleTuning:installOwnedKit', {
			vehicleId,
			kitId,
		});

		await this.fetchOwnedItemsSettings();
		await this.fetchOwnedFreeTuning();
	}
}
